<script>
import { Bar } from "vue-chartjs";
import Chart from "chart.js";
import ChartAnnotationsPlugin from "chartjs-plugin-annotation";
Chart.plugins.register(ChartAnnotationsPlugin);
import moment from "moment";
import _ from "lodash";
import { RainTankTopLevel } from "../../../enums/Type";

export default {
  extends: Bar,
  name: "RainTankChart",
  props: {
    selectedObservatory: {
      type: Object
    },
    displayInterval: Number
  },
  data() {
    return {
      dataCollection: {
        labels: [],
        datasets: [
          {
            type: "line",
            label: "水位",
            unit: "m",
            pointBackgroundColor: "white",
            fill: false,
            yAxisID: "level",
            borderWidth: 2,
            borderColor: "#01163F",
            backgroundColor: "#01163F",
            pointBorderColor: "#01163F",
            pointRadius: 2,
            pointHitRadius: 0,
            lineTension: 0,
            data: [],
            spanGaps: true
          }
        ]
      },
      options: {
        layout: {
          padding: {
            right: 0,
            left: 0
          }
        },
        tooltips: {
          enabled: true,
          mode: "index",
          intersect: false,
          callbacks: {
            label: function(tooltipItem, data) {
              const label = data.datasets[tooltipItem.datasetIndex].label;
              const unit = data.datasets[tooltipItem.datasetIndex].unit;
              return (
                label +
                " : " +
                data.datasets[tooltipItem.datasetIndex].data[
                  tooltipItem.index
                ] +
                " " +
                unit
              );
            }
          }
        },
        scales: {
          yAxes: [
            {
              id: "level",
              gridLines: {
                display: true
              },
              ticks: {
                max: 20,
                min: 0,
                stepSize: 1,
                callback: tick => {
                  return ("        " + tick).slice(-6);
                }
              }
            }
          ],
          xAxes: [
            {
              id: "data",
              type: "time",
              gridLines: {
                display: false
              },
              ticks: {
                display: true,
                autoSkip: false
              },
              time: {
                unit: "minute",
                stepSize: 10,
                tooltipFormat: "MM/DD HH:mm",
                displayFormats: {
                  minute: "HH:mm",
                  hour: "HH:mm",
                  day: "MM/DD"
                }
              },
              categoryPercentage: 1.0,
              barPercentage: 1.0
            }
          ]
        },
        legend: {
          display: false
        },
        responsive: true,
        maintainAspectRatio: false,
        hover: {
          mode: "index",
          intersect: false
        },
        groundDataset: [],
        annotation: {
          events: ["click", "dblclick", "mouseover", "mouseout"],
          annotations: [
            {
              type: "box",
              drawTime: "beforeDatasetsDraw",
              yScaleID: "level",
              borderColor: "rgba(0,0,0,0)",
              yMin: 0,
              yMax: 0,
              backgroundColor: "#3D8AFF"
            },
            {
              type: "line",
              mode: "vertical",
              scaleID: "data",
              value: "02:00",
              borderColor: "#FF0000",
              borderWidth: 1.5,
              label: {
                content: "現在",
                position: "top",
                enabled: true
              }
            }
          ]
        }
      }
    };
  },
  computed: {
    timeSeries() {
      if (this.selectedObservatory.timeSeries === undefined) {
        return [];
      } else {
        return this.selectedObservatory.timeSeries;
      }
    },
    baseDate() {
      return this.$store.getters.baseDate.clone();
    },
    minDate() {
      const d = this.baseDate.clone();
      if (this.displayInterval === 10) {
        d.subtract(3, "hours");
      } else if (this.displayInterval === 60) {
        d.subtract(2, "days");
      } else {
        d.subtract(14, "days");
      }
      return d.subtract(d.minute() % 10, "minutes");
    },
    maxDate() {
      if (this.displayInterval === 10) {
        return this.baseDate.clone().add(10, "minutes");
      } else if (this.displayInterval === 60) {
        return this.baseDate.clone().add(3, "hours");
      } else {
        return this.baseDate.clone().add(1, "days");
      }
    },
    landform() {
      const topLevel = RainTankTopLevel[this.selectedObservatory.id];
      if (topLevel === undefined) {
        return [];
      } else {
        return [
          [0, topLevel],
          [1, topLevel],
          [1, 0.0],
          [9, 0.0],
          [9, topLevel],
          [10, topLevel]
        ];
      }
    }
  },
  watch: {
    displayInterval() {
      this.reRender();
    },
    selectedObservatory() {
      this.reRender();
    }
  },
  mounted() {
    this.setTimeSeries();
    this.addPlugin({
      id: "horizontalLine",
      beforeDatasetsDraw: chart => {
        const groundDataset = chart.config.options.groundDataset;
        if (groundDataset.length > 0) {
          const ctxPlugin = chart.chart.ctx;
          const xAxe = chart.scales[chart.config.options.scales.xAxes[0].id];
          const yAxe = chart.scales[chart.config.options.scales.yAxes[0].id];
          const minGround = _.minBy(groundDataset, o => {
            return o[0];
          });
          const maxGround = _.maxBy(groundDataset, o => {
            return o[0];
          });
          ctxPlugin.strokeStyle = "red";
          ctxPlugin.beginPath();
          ctxPlugin.moveTo(xAxe.left, yAxe.bottom);
          for (let i = 0; i < groundDataset.length; i++) {
            let x = groundDataset[i][0] - minGround[0];
            x = (x / (maxGround[0] - minGround[0])) * xAxe.width + xAxe.left;
            let y = yAxe.max - yAxe.min - (groundDataset[i][1] - yAxe.min);
            y = (y / (yAxe.max - yAxe.min)) * yAxe.height + yAxe.top;
            ctxPlugin.lineTo(x, y);
          }
          ctxPlugin.lineTo(xAxe.right, yAxe.bottom);
          ctxPlugin.lineTo(xAxe.left, yAxe.bottom);
          ctxPlugin.closePath();
          ctxPlugin.fillStyle = "#f1eeea";
          ctxPlugin.fill();
        }
      }
    });
    setTimeout(() => {
      this.renderChart(this.dataCollection, this.options);
    }, 500);
  },
  methods: {
    reRender() {
      this.setTimeSeries();
      this.renderChart(this.dataCollection, this.options);
    },
    setTimeSeries() {
      this.dataCollection.labels = this.timeSeries.map(ts => {
        if (this.displayInterval === 60 && ts.date.substr(-2) !== "00") {
          return null;
        } else if (
          this.displayInterval === 24 &&
          ts.date.substr(-5) !== "15:00"
        ) {
          return null;
        }
        return moment
          .utc(ts.date, "YYYY/MM/DD HH:mm")
          .local()
          .toDate();
      });

      this.options.scales.xAxes[0].ticks.min = this.minDate.toDate();
      this.options.scales.xAxes[0].ticks.max = this.maxDate.toDate();
      this.options.scales.xAxes[0].time.unit =
        this.displayInterval === 10
          ? "minute"
          : this.displayInterval === 60
          ? "hour"
          : "day";
      this.options.scales.xAxes[0].time.stepSize =
        this.displayInterval === 10 ? 10 : 1;

      // reals
      this.dataCollection.datasets[0].data = this.timeSeries.map(ts => {
        if (this.displayInterval === 60 && ts.date.substr(-2) !== "00") {
          return null;
        } else if (
          this.displayInterval === 24 &&
          ts.date.substr(-5) !== "15:00"
        ) {
          return null;
        }
        return ts.level === undefined ? null : ts.level.toFixed(2);
      });

      this.options.groundDataset = this.landform;
      this.setYAxesLimitData();
      this.setNowStrokeData();
      this.setWaterBoxData();
    },
    setYAxesLimitData() {
      let levels = this.dataCollection.datasets[0].data;
      levels = levels.filter(l => l !== null).map(l => parseFloat(l));

      let min = _.min(levels) - 1;
      let max = _.max(levels) + 5;
      if (this.selectedObservatory.outbreakLevel !== undefined) {
        max = Math.floor(this.selectedObservatory.outbreakLevel) + 3;
      } else if (this.selectedObservatory.dangerousLevel !== undefined) {
        max = Math.floor(this.selectedObservatory.dangerousLevel) + 3;
      }

      if (this.landform.length > 0) {
        const landformYMin = _.minBy(this.landform, o => {
          return o[1];
        });
        const landformYMax = _.maxBy(this.landform, o => {
          return o[1];
        });
        if (landformYMin) {
          const landformYMinValue = Math.floor(landformYMin[1] - 1);
          if (landformYMinValue < min) {
            min = landformYMinValue;
          }
        }
        if (landformYMax) {
          const landformYMaxValue = Math.floor(landformYMax[1] + 1);
          if (max < landformYMaxValue) {
            max = landformYMaxValue;
          }
        }
      }
      if (this.selectedObservatory.bravegate === true) {
        max = 20;
      }
      if (["890_4", "890_5"].includes(this.selectedObservatory.id)) {
        max = 3;
      }
      this.options.scales.yAxes[0].ticks.min = Math.floor(min);
      this.options.scales.yAxes[0].ticks.max = Math.floor(max);
      this.options.annotation.annotations[0].yMin = Math.floor(min);
    },
    setNowStrokeData() {
      this.options.annotation.annotations[1].value = this.baseDate
        .clone()
        .local()
        .toDate();
    },
    setWaterBoxData() {
      const levels = this.timeSeries
        .filter(ts => ts.level !== undefined)
        .map(d => d.level);
      if (levels.length === 0) {
        return;
      }
      const targetLevel = levels[levels.length - 1];
      this.options.annotation.annotations[0].yMin = this.options.scales.yAxes[0].ticks.min;
      this.options.annotation.annotations[0].yMax = targetLevel;
    }
  }
};
</script>

<style scoped></style>
